import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export interface StageMeta {
  id: string;
  name: string;
  key: string | null;
  isIncharge: boolean;
}

interface StageMetaContextValue {
  stageMeta: StageMeta | null;
  refresh: () => void;
}

const StageMetaContext = createContext<StageMetaContextValue | undefined>(undefined);

const parseStageMeta = (raw: string | null): StageMeta | null => {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    const id = typeof parsed.id === 'string' ? parsed.id : '';
    const name = typeof parsed.name === 'string' ? parsed.name : '';
    if (!id || !name) {
      return null;
    }
    const key = typeof parsed.key === 'string' || parsed.key === null ? parsed.key : null;
    const isIncharge = Boolean(parsed.isIncharge);

    return {
      id,
      name,
      key,
      isIncharge,
    };
  } catch (error) {
    console.warn('Failed to parse stage meta from storage:', error);
    return null;
  }
};

const readStoredMeta = (): StageMeta | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  return parseStageMeta(window.localStorage.getItem('selectedStageMeta'));
};

export const StageMetaProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [stageMeta, setStageMeta] = useState<StageMeta | null>(() => readStoredMeta());

  const refresh = useCallback(() => {
    setStageMeta(readStoredMeta());
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleStorageChange = (event: StorageEvent) => {
      // Listen for selectedStageMeta changes (including isIncharge updates from permissionService)
      // Also listen for stagePermissions as it's dispatched alongside selectedStageMeta updates
      if (event.key === 'selectedStageMeta' || event.key === 'selectedStageId' || event.key === 'selectedStageName' || event.key === 'stagePermissions') {
        refresh();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [refresh]);

  const value = useMemo<StageMetaContextValue>(() => ({ stageMeta, refresh }), [stageMeta, refresh]);

  return <StageMetaContext.Provider value={value}>{children}</StageMetaContext.Provider>;
};

export const useStageMeta = (): StageMetaContextValue => {
  const context = useContext(StageMetaContext);
  if (!context) {
    throw new Error('useStageMeta must be used within a StageMetaProvider');
  }
  return context;
};


